import React from 'react';
import { Box, Grid, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { Field, Form } from 'formik';
import { Trans, useTranslation } from 'i18n';
import { CheckboxWithLabel } from 'formik-mui';
import IconButton from '@mui/material/IconButton';
import { InfoOutlined, Visibility, VisibilityOff } from '@mui/icons-material';

import { FieldToTextField } from 'components/common/FormField';
import Agreement from 'components/common/Agreement';
import MarketsField from 'components/fields/MarketsField';
import Button from 'components/common/Button';
import { makeStyles } from 'tss-react/mui';
import withJoinForm, { FormComponentProps } from './withJoinForm';

const agreementProps = {
  tOptions: {
    context: 'age',
  },
};

const useStyles = makeStyles()(() => ({
  market: {
    '.MuiInputBase-formControl': {
      height: 40,
      color: '#ACADB0',
    },
  },
  marketSelected: {
    '.MuiInputBase-formControl': {
      height: 40,
    },
  },
}));

const PasswordRequirementsTooltip: React.FC<{ moreInfoLabel: string }> = ({ moreInfoLabel }) => (
  <Tooltip
    title={
      <Typography variant="body2">
        <Trans i18nKey="modal.resetPassword.description" ns="public">
          <div>Strong passwords include:</div>
          <div>∙ 8+ characters</div>
          <div>∙ at least 1 upper case and lowercase</div>
          <div>∙ at least 1 number</div>
          <div>∙ at least 1 special character</div>
        </Trans>
      </Typography>
    }
  >
    <IconButton aria-label={moreInfoLabel} tabIndex={0} size="small">
      <InfoOutlined fontSize="small" />
    </IconButton>
  </Tooltip>
);

const JoinForm: React.FC<React.PropsWithChildren<FormComponentProps>> = (props) => {
  const { user, isSubmitting, values, withButton = true, disabledEmail = true, withPassword = false } = props;
  const [t] = useTranslation('public');
  const { classes } = useStyles();

  const [showPasswordOne, setShowPasswordOne] = React.useState(false);
  const [showPasswordTwo, setShowPasswordTwo] = React.useState(false);

  const handleClickShowPasswordOne = () => setShowPasswordOne((show) => !show);
  const handleMouseDownPasswordOne = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleClickShowPasswordTwo = () => setShowPasswordTwo((show) => !show);
  const handleMouseDownPasswordTwo = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Grid container spacing={3} component={Form}>
      <Grid item xs={12}>
        <Field
          fullWidth
          name="email"
          variant="outlined"
          placeholder={`${t<string>('common:field.label.email')}*`}
          disabled={Boolean(user?.email) && disabledEmail}
          component={FieldToTextField}
          TextField={TextField}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field
          fullWidth
          name="first_name"
          variant="outlined"
          placeholder={`${t<string>('common:field.label.first_name')}*`}
          component={FieldToTextField}
          TextField={TextField}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field
          fullWidth
          name="last_name"
          variant="outlined"
          placeholder={`${t<string>('common:field.label.last_name')}*`}
          component={FieldToTextField}
          TextField={TextField}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MarketsField
              className={
                !values.primary_market && values.primary_market !== 0 ? classes.market : classes.marketSelected
              }
              onlyLocal
              fullWidth
              name="primary_market"
              variant="outlined"
              placeholder={`${t<string>('common:field.placeholder.primary_city')}*`}
            />
          </Grid>
          {withPassword && (
            <>
              <Grid item xs={12} md={6}>
                <Field
                  fullWidth
                  name="password_one"
                  variant="outlined"
                  placeholder={t<string>('common:field.label.create_password_one')}
                  type={showPasswordOne ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <PasswordRequirementsTooltip moreInfoLabel={t<string>('common:general.ctaButton_more_info')} />
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPasswordOne}
                          onMouseDown={handleMouseDownPasswordOne}
                          edge="end"
                          size="small"
                        >
                          {showPasswordOne ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  component={FieldToTextField}
                  TextField={TextField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  fullWidth
                  name="password_two"
                  variant="outlined"
                  placeholder={t<string>('common:field.label.create_password_two')}
                  type={showPasswordTwo ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <PasswordRequirementsTooltip moreInfoLabel={t<string>('common:general.ctaButton_more_info')} />
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPasswordTwo}
                          onMouseDown={handleMouseDownPasswordTwo}
                          edge="end"
                          size="small"
                        >
                          {showPasswordTwo ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  component={FieldToTextField}
                  TextField={TextField}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Field
              component={CheckboxWithLabel}
              name="age"
              type="checkbox"
              color="primary"
              Label={{
                label: <Agreement {...agreementProps} />,
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      {withButton && (
        <Grid item xs={12}>
          <Box mt={2} textAlign="center">
            <Button
              style={{ height: 40 }}
              rounded
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSubmitting || !values.age}
            >
              {t<string>('auth.joinForm.submit')}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export { JoinForm };
export default withJoinForm(JoinForm);
